import React, { useEffect, useRef, useState } from "react";

import moment from "moment";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFarmLandUseChanges,
  useGetLandUseChanges,
  useMutateFarmLandUseChanges,
  useTranslation,
} from "utils";

import { ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED, FARM_FOOTPRINTING_HAS_LAND_CHANGED, PRACTICE_SLUG, UploadableLandUseChange, ZERO_UUID } from "models";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  Option,
  PageSpin,
  Radio,
  Row,
  Select,
  SkeletonWrapper,
} from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const landUseChangePlaceholder: UploadableLandUseChange = {
  id: undefined,
  landUseChangeId: undefined,
  yearOfConversion: null,
  areaConvertedHa: null,
  hasLandChanged: null,
  isUnknown: false,
};
export const LandUseForm = ({ readOnly, farmId, year, primaryDataId }: FarmGeneralProps) => {
  const { t } = useTranslation("footprinting");
  const formRef = useRef<FormInstance>(null);
  const selectGroupName = "footprinting-farm-land-use-change";

  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);
  const startYear = carbonProgramQuery.data?.farmLevelFootprinting?.startYear;
  const isStartYear = startYear === year;

  const hasLandChangedFormRefKey = "hasLandChanged";
  const [hasLandChanged, setHasLandChanged] = useState<ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED | null>(null);
  const [selectedLandUseChanges, setSelectedLandUseChanges] = useState<UploadableLandUseChange[]>([]);
  const [loading] = useState<boolean>();

  const yearRange = isStartYear ? 20 : 1;
  const landUseChangeQuery = useGetLandUseChanges();
  const farmLandUseChangeUsageQuery = useGetFarmLandUseChanges(farmId, {
    year,
    range: yearRange,
  });

  const { mutate: mutateFarmLandUseChangeUsage } = useMutateFarmLandUseChanges(
    farmId,
    year,
    yearRange,
    {
      farmId,
      selectedLandUseChange:
        hasLandChanged === FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES
          ? selectedLandUseChanges
              .filter((change) => !!change.landUseChangeId)
              .map((change) => ({
                id: change.id,
                landUseChangeId: change.landUseChangeId,
                hasLandChanged: hasLandChanged,
                yearOfConversion: change.yearOfConversion,
                areaConvertedHa: change.areaConvertedHa,
                isUnknown: false,
                previousLandUseChange: change.previousLandUseChange,
              }))
          : [
              {
                hasLandChanged: hasLandChanged === FARM_FOOTPRINTING_HAS_LAND_CHANGED.UNKNOWN ? null : hasLandChanged,
                landUseChangeId: undefined,
                yearOfConversion: isStartYear ? startYear : year,
                areaConvertedHa: null,
                isUnknown: hasLandChanged === FARM_FOOTPRINTING_HAS_LAND_CHANGED.UNKNOWN,
              },
            ],
    },
    primaryDataId
  );

  const isLoading = [landUseChangeQuery, carbonProgramQuery].some(queryIsLoading) || (isStartYear && queryIsLoading(farmLandUseChangeUsageQuery));

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateFarmLandUseChangeUsage();
      }
    },
    [isLoading, mutateFarmLandUseChangeUsage, readOnly]
  );

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (farmLandUseChangeUsageQuery.data && !!farmLandUseChangeUsageQuery.data.length) {
      if (
        farmLandUseChangeUsageQuery.data.length === 1 &&
        (farmLandUseChangeUsageQuery.data.some((luc) => luc.hasLandChanged === FARM_FOOTPRINTING_HAS_LAND_CHANGED.NO) ||
          farmLandUseChangeUsageQuery.data.some((luc) => luc.isUnknown === true))
      ) {
        const landUseChangeState =
          farmLandUseChangeUsageQuery.data.some((luc) => luc.isUnknown) && isStartYear
            ? FARM_FOOTPRINTING_HAS_LAND_CHANGED.UNKNOWN
            : farmLandUseChangeUsageQuery.data.some((luc) => luc.hasLandChanged === FARM_FOOTPRINTING_HAS_LAND_CHANGED.NO)
            ? FARM_FOOTPRINTING_HAS_LAND_CHANGED.NO
            : null;

        setHasLandChanged(landUseChangeState);
        setSelectedLandUseChanges([]);
        formRef.current?.setFieldValue(hasLandChangedFormRefKey, landUseChangeState);
      } else {
        setHasLandChanged(FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES);
        setSelectedLandUseChanges(
          farmLandUseChangeUsageQuery.data.map((landUseChange) => ({
            ...landUseChange,
            hasLandChanged: hasLandChanged,
          }))
        );
        formRef.current?.setFieldValue(hasLandChangedFormRefKey, FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES);
      }

      if (formRef && formRef.current) {
        formRef.current.validateFields();
      }
    } else if (!queryIsLoading(farmLandUseChangeUsageQuery)) {
      // If we're not loading but the user has landUseChange active,
      // set the selectedLandUseChanges to the placeholder
      setSelectedLandUseChanges([{ ...landUseChangePlaceholder }]);
    }
  }, [farmLandUseChangeUsageQuery.data, isStartYear, year]); // eslint-disable-line react-hooks/exhaustive-deps

  // This ensures that all form fields rerender when the non-last land use change
  // is removed (i.e. they appear to slide up)
  useEffect(() => {
    if (selectedLandUseChanges.length > 0) {
      formRef.current?.resetFields();
    }
  }, [selectedLandUseChanges.length]);

  if (primaryDataId && isLoading) return <PageSpin />;

  const questionText = isStartYear ? t("landUseForm.question.general") : t("landUseForm.question.followingYearQuestion");

  const questionOptions = isStartYear
    ? [
        { value: FARM_FOOTPRINTING_HAS_LAND_CHANGED.NO, label: t("landUseForm.radioButton.no") },
        { value: FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES, label: t("landUseForm.radioButton.yes") },
        { value: FARM_FOOTPRINTING_HAS_LAND_CHANGED.UNKNOWN, label: t("landUseForm.radioButton.dontKnow") },
      ]
    : [
        { value: FARM_FOOTPRINTING_HAS_LAND_CHANGED.NO, label: t("landUseForm.radioButton.noFollowingYear") },
        { value: FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES, label: t("landUseForm.radioButton.yes") },
      ];

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-200 padding-right-200">{t("landUseForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{isStartYear ? t("landUseForm.infoStartYear") : t("landUseForm.infoFollowingYear")}</p>
        </Flex>
      </Card>

      <Card dataTestId="footprinting-farm-land-use-chages-form" className="margin-bottom-100 padding-200">
        <p className="paragraph-m-bold margin-top-0 padding-bottom-0">{questionText} </p>
        <Form ref={formRef}>
          <Form.Item name={hasLandChangedFormRefKey} rules={[{ message: t("landUseForm.radioButton.error") }]}>
            <Radio.Group onChange={(e) => setHasLandChanged(e.target.value)} value={hasLandChanged} defaultValue={hasLandChanged}>
              <Flex vertical gap={16} justify="space-between">
                {questionOptions.map((option) => (
                  <SkeletonWrapper key={option.value} className="margin-0 skeleton-rectangular" height="1rem" type="block" isLoading={loading}>
                    <Radio value={option.value}>{option.label}</Radio>
                  </SkeletonWrapper>
                ))}
              </Flex>
            </Radio.Group>
          </Form.Item>
          {hasLandChanged === FARM_FOOTPRINTING_HAS_LAND_CHANGED.YES && (
            <>
              {selectedLandUseChanges.map((landUseChange, index) => {
                const selectGroupWithIndex = `${selectGroupName}-${index}`;
                const updateItem = (item: UploadableLandUseChange) => {
                  const next = [...selectedLandUseChanges];
                  next[index] = { ...item };
                  setSelectedLandUseChanges(next);
                };
                return (
                  <div key={index} className="padding-top-200">
                    <Flex className="margin-bottom-100" justify="space-between">
                      <h3 style={{ flex: 1 }}>{t("landUseForm.question.title") + (index + 1)}</h3>
                      <IconButton
                        type="tertiary"
                        disabled={readOnly}
                        size="small"
                        iconName="tabler/trash"
                        onClick={() => setSelectedLandUseChanges([...selectedLandUseChanges].filter((_, i) => i !== index))}
                      />
                    </Flex>

                    {/* @ts-expect-error need to convert to tsx */}
                    <Select
                      className="margin-bottom-100"
                      name={selectGroupWithIndex}
                      disabled={readOnly}
                      dataTestId={selectGroupWithIndex}
                      label={t("landUseForm.question.previousLandChange")}
                      rules={[{ required: true, message: false }]}
                      initialValue={landUseChange.landUseChangeId}
                      value={landUseChange.landUseChangeId}
                      onChange={(v) => {
                        const selectedType = landUseChangeQuery.data?.find((l) => l.id === v);
                        updateItem({
                          ...landUseChange,
                          landUseChangeId: v,
                          previousLandUseChange: selectedType
                            ? {
                                id: v,
                                translationSlug: selectedType.translationSlug,
                              }
                            : undefined,
                        });
                      }}
                      dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                      allowClear
                    >
                      {(landUseChangeQuery.data ?? [])
                        .filter(({ id }) => id !== ZERO_UUID)
                        .map((practice) => (
                          <Option
                            disabled={readOnly}
                            key={`${selectGroupWithIndex}-${practice.id}`}
                            data-testid={`${selectGroupWithIndex}-${practice.id}`}
                            value={practice.id}
                          >
                            {t(`${PRACTICE_SLUG.LAND_USE_CHANGES}.${practice.translationSlug}`, { ns: "practices" }) || practice.translationSlug}
                          </Option>
                        ))}
                    </Select>

                    <Row gutter={0}>
                      <Col xs={24} sm={12}>
                        <Form.Item className="padding-right-50">
                          <DatePicker
                            name={`${selectGroupWithIndex}-conversionYear`}
                            dataTestId={`${selectGroupWithIndex}-conversionYear`}
                            label={t("landUseForm.question.conversionYear")}
                            format="YYYY"
                            picker="year"
                            disabled={readOnly}
                            value={landUseChange.yearOfConversion ? moment(landUseChange.yearOfConversion, "YYYY") : null}
                            initialValue={landUseChange.yearOfConversion ? moment(landUseChange.yearOfConversion, "YYYY") : null}
                            onChange={(date) => {
                              const yearOfConversion = date ? date.year() : null;
                              updateItem({ ...landUseChange, yearOfConversion });
                            }}
                            disabledDate={(y) => (isStartYear ? y.year() < year - 20 || y.year() > year : y.year() !== year)}
                            rules={[{ required: true, message: t("landUseForm.question.landUseChangeYearQuestionError") }]}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item className="padding-left-50">
                          <InputNumber
                            name={`${selectGroupWithIndex}-areaConverted`}
                            dataTestId={`${selectGroupWithIndex}-areaConverted`}
                            label={t("landUseForm.question.areaConverted")}
                            suffix="Ha"
                            disabled={readOnly}
                            value={landUseChange.areaConvertedHa}
                            initialValue={landUseChange.areaConvertedHa}
                            onChange={(v) => updateItem({ ...landUseChange, areaConvertedHa: v })}
                            rules={[
                              { required: true, message: t("landUseForm.question.landUseChangeAreaQuestionError") },
                              { type: "number" },
                              {
                                validator: (_: unknown, v: number) =>
                                  isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject(),
                                message: t("landUseForm.question.landUseChangeAreaQuestionError"),
                              },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                );
              })}
              <Button
                type="text"
                disabled={readOnly}
                className="margin-top-125 padding-left-25"
                onClick={() => {
                  setSelectedLandUseChanges([...selectedLandUseChanges, { ...landUseChangePlaceholder }]);
                }}
              >
                <Icon iconName="klim/plus" className="icon inline" /> {t("landUseForm.add")}
              </Button>
            </>
          )}
        </Form>
      </Card>
    </div>
  );
};
